import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useQuery } from "@tanstack/react-query";
import { Menu, X } from "lucide-react";
import { api, track, captureSource } from "@/lib/api";
import { Mark7, Wordmark, Ornament, Tagline, Btn, Diamond } from "@/components/brand";

export const DEFAULT_SQUARE = "https://checkout.square.site/merchant/ML5PRPQ6DE1TQ/checkout/V7R6HXFQHXRLNGAA7PFVKY5R";
export const PAY_COPY = "Pay securely through Square: any debit or credit card, Cash App Pay, Apple Pay, or split it into interest-free installments with Afterpay. Need a different plan? Message me and I'll work with you.";

export function useSite() {
  return useQuery({ queryKey: ["site"], queryFn: async () => (await api.get("/public/site")).data });
}

export function useSeo(title, description) {
  useEffect(() => {
    document.title = title ? `${title} | Se7en Capital Group` : "Se7en Capital Group — Credit Repair, Inquiry Sweeps & Funding";
    if (description) document.querySelector('meta[name="description"]')?.setAttribute("content", description);
  }, [title, description]);
}

export function Logo({ dark = false, to = "/" }) {
  return (
    <Link to={to} className="flex items-center gap-3" data-testid="brand-logo-link" aria-label="Se7en Capital Group home">
      <Mark7 size={38} />
      <Wordmark dark={dark} />
    </Link>
  );
}

const NAV = [
  ["Services", "/#services"], ["Results", "/results"], ["Pricing", "/#pricing"], ["Reviews", "/#reviews"],
];

function Header() {
  const [open, setOpen] = useState(false);
  const { data } = useSite();
  const loc = useLocation();
  const { user } = useAuth();
  const portal = user ? (user.role === "client" ? ["/portal", "My Portal"] : ["/owner", "Owner Portal"]) : null;
  useEffect(() => { setOpen(false); }, [loc.pathname, loc.hash]);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; }, [open]);
  const nav = NAV.filter(([l]) => l !== "Reviews" || (data?.reviews?.length > 0));
  return (
    <header className="sticky top-0 z-40 bg-white/[.97] backdrop-blur-md border-b border-tanline" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7 text-[14px] font-medium" aria-label="Main">
          {nav.map(([l, h]) => <a key={l} href={h} className="nav-link" data-testid={`nav-${l.toLowerCase()}`}>{l}</a>)}
          <NavLink to="/refer" className="nav-link font-bold text-red7" data-testid="nav-refer">Refer & Earn $100</NavLink>
          {portal ? <Link to={portal[0]} className="nav-link font-semibold" data-testid="nav-my-portal">{portal[1]} →</Link> : <>
            <NavLink to="/login" className="nav-link" data-testid="nav-client-login">Client Login</NavLink>
            <NavLink to="/owner/login" className="nav-link text-muted7" data-testid="nav-owner-login">Owner Login</NavLink></>}
          <Btn as={Link} to="/consultation" variant="black" className="btn-sm" data-testid="header-consult-btn" onClick={() => track("cta_click", { label: "header_consult" })}>Free Consultation</Btn>
        </nav>
        <button className="lg:hidden w-11 h-11 flex items-center justify-center" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="mobile-menu-btn"><Menu /></button>
      </div>
      {open && createPortal(
        <div className="fixed inset-0 z-[60] bg-ink text-white flex flex-col overflow-y-auto" role="dialog" aria-modal="true" data-testid="mobile-menu">
          <div className="h-[72px] px-5 flex items-center justify-between border-b border-white/10">
            <Logo dark />
            <button className="w-11 h-11 flex items-center justify-center" onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close"><X /></button>
          </div>
          <nav className="flex-1 px-6 py-8 flex flex-col gap-1">
            {nav.map(([l, h]) => <a key={l} href={h} onClick={() => setOpen(false)} className="f-display text-3xl py-3 border-b border-white/10">{l}</a>)}
            <Link to="/refer" className="f-display text-3xl py-3 border-b border-white/10 text-red7b">Refer & Earn $100</Link>
            {portal ? <Link to={portal[0]} className="f-display text-3xl py-3 border-b border-white/10 text-gold" data-testid="mobile-my-portal">{portal[1]} →</Link> : <>
              <Link to="/login" className="f-display text-3xl py-3 border-b border-white/10">Client Login</Link>
              <Link to="/owner/login" className="f-display text-xl py-3 text-white/60">Owner Login</Link></>}
          </nav>
          <div className="p-6"><Btn as={Link} to="/consultation" variant="gold" arrow className="w-full" data-testid="mobile-consult-btn">Book Free Consultation</Btn></div>
        </div>,
        document.body
      )}
    </header>
  );
}

export function Ticker() {
  const items = ["FASTEST & MOST AFFORDABLE IN THE INDUSTRY", "ONE COMPLETE SWEEP", "RESULTS IN AS LITTLE AS 4–7 DAYS", "$20K–$100K+ FUNDING", "TALK TO SEVEN ANYTIME", "$100 FOR EVERY REFERRAL", "INTEREST-FREE INSTALLMENTS"];
  const row = items.map((t, i) => (
    <span key={i} className="flex items-center gap-6 px-6"><span>{t}</span><Diamond className="!w-2 !h-2 bg-red7b" /></span>
  ));
  return (
    <div className="ticker bg-ink text-gold f-mono text-[11px] sm:text-xs tracking-[0.2em] py-4 overflow-hidden" data-testid="highlight-ticker" aria-label={items.join(", ")}>
      <div className="ticker-track" aria-hidden="true">{row}{row}</div>
    </div>
  );
}

function Footer() {
  const { data } = useSite();
  const b = data?.business || {};
  return (
    <footer className="bg-white border-t-[3px] border-red7" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-10 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <Wordmark />
          <p className="text-sm text-muted7 mt-5 max-w-xs">Credit repair, inquiry sweeps and funding — handled personally by Seven.</p>
          <ul className="mt-5 space-y-2 text-sm">
            {b.phone && <li><a className="nav-link" href={`tel:${b.phone}`} data-testid="footer-phone">{b.phone}</a></li>}
            {b.email && <li><a className="nav-link" href={`mailto:${b.email}`} data-testid="footer-email">{b.email}</a></li>}
            {b.tiktok && <li><a className="nav-link" href={b.tiktok.startsWith("http") ? b.tiktok : `https://www.tiktok.com/@${b.tiktok.replace("@", "")}`} target="_blank" rel="noreferrer" data-testid="footer-tiktok">TikTok</a></li>}
          </ul>
        </div>
        <div>
          <p className="eyebrow text-goldtext mb-4">Services</p>
          <ul className="space-y-2.5 text-sm">
            {["Credit Repair", "Inquiry Sweep · $97", "Funding · $20k–$100k+", "Business Opening & LLC", "Financial Consulting", "Real Estate Strategies"].map((s) => <li key={s}><a href="/#services" className="nav-link">{s}</a></li>)}
          </ul>
        </div>
        <div>
          <p className="eyebrow text-goldtext mb-4">Portals</p>
          <ul className="space-y-2.5 text-sm">
            <li><Link className="nav-link" to="/login">Client Login</Link></li>
            <li><Link className="nav-link" to="/register">New Client Intake</Link></li>
            <li><Link className="nav-link" to="/owner/login">Owner Login</Link></li>
          </ul>
        </div>
        <div>
          <p className="eyebrow text-goldtext mb-4">Refer & earn</p>
          <p className="text-sm text-muted7 mb-5">Get ${data?.referralPayout || 100} for every person you send who enrolls.</p>
          <Btn as={Link} to="/refer" variant="red" className="btn-sm" data-testid="footer-refer-btn">Refer Someone</Btn>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-5 pb-10"><Ornament className="mb-4" /><Tagline /></div>
      <div className="border-t border-tanline">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-col sm:flex-row gap-3 justify-between text-xs text-muted7">
          <span>© {new Date().getFullYear()} Se7en Capital Group · se7encapital.com</span>
          <span className="flex gap-5"><Link to="/privacy" className="nav-link">Privacy</Link><Link to="/terms" className="nav-link">Terms</Link><span>Not a law firm. Results vary.</span></span>
        </div>
      </div>
    </footer>
  );
}

function CookieNotice() {
  const [show, setShow] = useState(() => !localStorage.getItem("s7_cookie"));
  if (!show) return null;
  const choose = (v) => { localStorage.setItem("s7_cookie", v); setShow(false); };
  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:max-w-sm z-50 bg-ink text-white p-5 shadow-2xl border-l-4 border-gold fade-up" role="dialog" aria-label="Cookie notice" data-testid="cookie-notice">
      <p className="text-sm text-white/85">I use first-party cookies to keep the site working and to see which pages help people. No ad pixels.</p>
      <div className="flex gap-3 mt-4">
        <button className="btn btn-gold btn-sm" onClick={() => choose("accepted")} data-testid="cookie-accept">Accept</button>
        <button className="btn btn-outline-light btn-sm" onClick={() => choose("declined")} data-testid="cookie-decline">Decline non-essential</button>
      </div>
    </div>
  );
}

export function PublicLayout() {
  const loc = useLocation();
  useEffect(() => {
    captureSource();
    track("page_view");
    if (!loc.hash) window.scrollTo(0, 0);
    else setTimeout(() => document.getElementById(loc.hash.slice(1))?.scrollIntoView(), 80);
  }, [loc.pathname, loc.hash]);
  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-gold focus:p-3">Skip to content</a>
      <Header />
      <main id="main" className="flex-1"><Outlet /></main>
      <Footer />
      <CookieNotice />
    </div>
  );
}
